import React from "react";
import { NavLink, Link } from "react-router-dom";
import {
  HouseSimple,
  CircuitryIcon,
  BellSimple,
  TimerIcon,
  ClockCounterClockwise,
  GearSix,
  ArrowSquareOut,
  SignOut,
  User,
  Wallet,
  ShieldStar,
  Robot,
  Newspaper
} from "@phosphor-icons/react";
import { AUREUS } from "@/constants/testIds";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

const NAV = [
  { to: "/dashboard",         label: "Markets",   icon: HouseSimple,            testId: AUREUS.navHome },
  { to: "/engines",           label: "Engines",   icon: CircuitryIcon,          testId: AUREUS.navEngines },
  { to: "/timeline",          label: "Timeline",  icon: TimerIcon,              testId: AUREUS.navTimeline },
  { to: "/history",           label: "History",   icon: ClockCounterClockwise,  testId: AUREUS.navHistory },
  { to: "/news",              label: "News Risk", icon: Newspaper,              testId: "nav-news" },
  { to: "/alerts",            label: "Alerts",    icon: BellSimple,             testId: AUREUS.navAlerts,   stub: true },
  { to: "/auto-trading",      label: "Auto Trading", icon: Robot,               testId: "nav-auto-trading" },
  { to: "/billing",           label: "Billing",   icon: Wallet,                 testId: "nav-billing" },
  { to: "/settings",          label: "Settings",  icon: GearSix,                testId: AUREUS.navSettings, stub: true },
];

export default function Sidebar({ sseStatus = "connecting" }) {
  const { user, logout } = useAuth();
  // Unread alerts badge — only when authenticated
  const { data: alertsData } = useQuery({
    queryKey: ["alerts-unread-badge"],
    queryFn: () => api.get("/alerts", { params: { limit: 1, unread_only: true } }).then((r) => r.data),
    enabled: !!user,
    refetchInterval: 5000,
  });
  const unread = alertsData?.unread || 0;
  const dotColor =
    sseStatus === "live" ? "bg-bull" :
    sseStatus === "reconnecting" ? "bg-amber-400" : "bg-neutral_clr";
  return (
    <aside
      data-testid={AUREUS.sidebar}
      className="fixed left-0 top-0 h-full w-64 bg-ink-950 border-r border-white/10 flex flex-col z-40"
    >
      <Link
        to="/"
        data-testid={AUREUS.sidebarLogo}
        className="px-6 py-6 border-b border-white/10 flex items-center gap-2 group"
      >
        <span className="heading text-2xl font-extrabold tracking-tighter">
          AUREUS<span className="text-gold-grad">.AI</span>
        </span>
      </Link>

      <nav className="flex flex-col gap-1 p-4 flex-1">
        <div className="overline px-3 pb-2">Workspace</div>
        {NAV.map(({ to, label, icon: Icon, testId, withBadge }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={testId}
            className={({ isActive }) => `nav-link ${isActive ? "nav-link-active" : ""}`}
          >
            <Icon size={18} weight="regular" />
            <span className="flex-1">{label}</span>
            {withBadge && unread > 0 && (
              <span data-testid="sidebar-alerts-badge" className="mono text-[10px] bg-gold text-black px-1.5 py-0.5 rounded-sm font-semibold">
                {unread > 99 ? "99+" : unread}
              </span>
            )}
          </NavLink>
        ))}

        <div className="mt-auto pt-4 border-t border-white/10">
          {user && (
            <div className="px-3 py-3 flex items-center gap-3">
              <div className="w-8 h-8 rounded-sm bg-gold/15 border border-gold/30 flex items-center justify-center">
                <User size={14} className="text-gold" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium truncate" data-testid="sidebar-user-name">{user.name}</div>
                <div className="mono text-[10px] text-zinc-500 truncate">{user.email}</div>
              </div>
            </div>
          )}
          {user?.role === "admin" && (
            <Link
              to="/admin"
              data-testid="nav-admin"
              className="nav-link"
            >
              <ShieldStar size={18} />
              <span>Admin</span>
            </Link>
          )}
          {user && (
            <button
              type="button"
              onClick={logout}
              data-testid="sidebar-logout"
              className="nav-link w-full"
            >
              <SignOut size={18} />
              <span>Sign out</span>
            </button>
          )}
          <Link to="/" data-testid={AUREUS.navLanding} className="nav-link">
            <ArrowSquareOut size={18} />
            <span>Landing</span>
          </Link>
          <div className="px-3 py-3 mt-2 flex items-center gap-2 text-xs">
            <span className={`inline-block w-2 h-2 rounded-full ${dotColor} ${sseStatus === "live" ? "animate-pulse-dot" : ""}`} />
            <span className="mono text-zinc-500 uppercase tracking-widest text-[10px]">
              stream {sseStatus}
            </span>
          </div>
        </div>
      </nav>
    </aside>
  );
}
